import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Heart, User, Search, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/shop", label: "Shop" },
    { to: "/build-a-box", label: "Build a Box" },
    { to: "/catering", label: "Catering" },
    { to: "/about", label: "Our Story" },
    { to: "/reviews", label: "Reviews" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const { count, setOpen } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);

    const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

    return (
        <header className="sticky top-0 z-50 border-b border-caramel/10 bg-cream-50/80 backdrop-blur-xl">
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-8">
                {/* Left: mobile menu + logo */}
                <div className="flex items-center gap-3">
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <button data-testid="mobile-menu-btn" className="lg:hidden grid h-10 w-10 place-items-center rounded-full text-roast hover:bg-cream-200 transition-colors" aria-label="Open menu">
                                <Menu size={22} />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-[300px] bg-cream-50 border-caramel/10 p-0">
                            <SheetTitle className="sr-only">Menu</SheetTitle>
                            <SheetDescription className="sr-only">Main navigation links.</SheetDescription>
                            <div className="flex items-center justify-between px-6 py-5 border-b border-caramel/10">
                                <span className="font-script text-3xl text-black">Sweet Pearls</span>
                            </div>
                            <nav className="flex flex-col p-3">
                                {LINKS.map((l) => (
                                    <Link
                                        key={l.to}
                                        to={l.to}
                                        data-testid={`m-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        onClick={() => setMobileOpen(false)}
                                        className={`rounded-xl px-4 py-3 text-base font-medium transition-colors ${isActive(l.to) ? "bg-banana/30 text-roast" : "text-roast/80 hover:bg-cream-200"}`}
                                    >
                                        {l.label}
                                    </Link>
                                ))}
                            </nav>
                        </SheetContent>
                    </Sheet>

                    <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
                        <Heart size={26} className="fill-banana text-banana transition-transform group-hover:scale-110" data-testid="logo-heart" />
                        <span className="font-script text-3xl leading-none text-black">
                            Sweet Pearls
                        </span>
                    </Link>
                </div>

                {/* Center nav */}
                <nav className="hidden lg:flex items-center gap-1">
                    {LINKS.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${isActive(l.to) ? "text-caramel" : "text-roast/70 hover:text-roast"}`}
                        >
                            {l.label}
                            {isActive(l.to) && <span className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-banana-deep" />}
                        </Link>
                    ))}
                </nav>

                {/* Right icons */}
                <div className="flex items-center gap-1">
                    <button data-testid="search-btn" onClick={() => navigate("/shop")} className="grid h-10 w-10 place-items-center rounded-full text-roast hover:bg-cream-200 transition-colors" aria-label="Search">
                        <Search size={20} />
                    </button>
                    <Link to={user ? "/account" : "/login"} data-testid="favorites-btn" className="hidden sm:grid h-10 w-10 place-items-center rounded-full text-roast hover:bg-cream-200 transition-colors" aria-label="Favorites">
                        <Heart size={20} />
                    </Link>
                    <Link to={user ? "/account" : "/login"} data-testid="account-btn" className="grid h-10 w-10 place-items-center rounded-full text-roast hover:bg-cream-200 transition-colors" aria-label="Account">
                        <User size={20} />
                    </Link>
                    <button data-testid="cart-btn" onClick={() => setOpen(true)} className="relative grid h-10 w-10 place-items-center rounded-full text-roast hover:bg-cream-200 transition-colors" aria-label="Cart">
                        <ShoppingBag size={20} />
                        {count > 0 && (
                            <span data-testid="cart-count" className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-caramel px-1 text-[11px] font-semibold text-cream-50">
                                {count}
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </header>
    );
}
